import produce, { immerable } from 'immer';
import create, { StoreApi } from 'zustand';

/**
 * Right now a pretty atomic piece of state, but expandable. The main reason for it's existence, now (Mobile Squad),
 * is to avoid Forms code referring to Mobile-Specific code all the time.
 */
class DialogStore {
  //required to use immer on a class
  [immerable] = true;

  /**
   * For situations a dialog title needs to be read / updated after the fact, e.g. coming from form surface,
   * not flow state. This is also a zustand store, since it needs to apply to Classic Forms, too
   */
  dialogTitlesByInstanceId = new Map<string, string>();
  openDialogs = new Map<number, () => void>();

  actions = {
    setDialogTitle: (id: string, title: string) => {
      if (!title) {
        // Don't bother saving a falsy title.
        // Also the Simple Form loading chain will clear it out because onFormLayoutComplete is called later there w/ null title
        return;
      }
      this.set(
        produce<DialogStore>(state => {
          state.dialogTitlesByInstanceId.set(id, title);
        })
      );
    },

    clearDialogTitle: (id: string) => {
      this.set(
        produce<DialogStore>(state => {
          state.dialogTitlesByInstanceId.delete(id);
        })
      );
    },

    setDialogOpen: (closeDialog: () => void) => {
      const key = this.get().openDialogs.size + 1;
      this.set(
        produce<DialogStore>(state => {
          state.openDialogs.set(key, closeDialog);
        })
      );
      return key;
    },

    setDialogClosed: (id: number | undefined) => {
      if (id) {
        this.set(
          produce<DialogStore>(state => {
            state.openDialogs.delete(id);
          })
        );
      }
    },

    closeAllDialogs: () => {
      Array.from(this.get().openDialogs.values()).forEach(closeDialog => closeDialog());
      this.openDialogs = new Map();
    },
  };

  constructor(
    private set: StoreApi<DialogStore>['setState'],
    private get: StoreApi<DialogStore>['getState'] // private store: Mutate<StoreApi<FormStore>, [/* add middleware mutators here, if we need them */]>
  ) {}
}

export const useDialogStore = create<DialogStore>()((set, get, _store) => new DialogStore(set, get));

//#region hooks

export function useDialogTitle(id: string, title?: string) {
  const formTitle = useDialogStore(state => state.dialogTitlesByInstanceId.get(id));
  // if someone went to the trouble to set this, override the title prop
  return formTitle || title || '';
}

//#endregion hooks
